import React, { useState, useRef, useCallback, useEffect } from 'react';
import axios from '../../axios';
import config from '../../config';
import './unifiedFileUpload.css';
import { useLanguage } from '../../i18n/LanguageContext';

function UnifiedFileUpload({ 
  data_id, 
  existingImages = [], 
  onImagesUpdate, 
  allowMultiple = true, 
  maxFiles = 10,
  acceptTypes = "image/*",
  uploadEndpoint,
  delayUpload = false,  // New option for delayed upload in forms
  onFilesChange = null,   // New callback for parent to get access to selected files
  onPreviewUrlsChange = null,  // New callback for parent to get access to preview URLs
  onImageClick = null   // New prop to handle image clicks for fullscreen view
}) {
  const [files, setFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [uploadedImages, setUploadedImages] = useState(existingImages);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [isMobileOrTablet, setIsMobileOrTablet] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const { t } = useLanguage();
  
  // Check if the device is mobile or tablet
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      
      // Comprehensive mobile and tablet detection
      const mobileTabletRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile|tablet|kindle|playbook|silk/i;
      const isIPad = /iPad/.test(userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      
      // Additional check for tablet devices that may not be caught by regex alone
      const isTablet = /(tablet|ipad|playbook|silk)|(android(?!.*mobile))/i.test(userAgent.toLowerCase());
      
      const mobileOrTablet = mobileTabletRegex.test(userAgent) || isTablet || isIPad;
      
      setIsMobileOrTablet(mobileOrTablet);
    };
    
    checkMobile();
  }, []);
  
  // Ensure uploadedImages stays in sync with existingImages
  useEffect(() => {
    setUploadedImages(existingImages);
  }, [existingImages]);
  
  // Handle drag events
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);
  
  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);
  
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) setIsDragging(true);
  }, [isDragging]);

  // Handle camera capture
  const handleCameraCapture = useCallback(() => {
    if (cameraInputRef.current) {
      cameraInputRef.current.click();
    }
  }, []);
  
  // Handle camera input change
  const handleCameraInputChange = useCallback((e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  }, []);

  // Handle file selection or drop
  const handleFiles = useCallback((selectedFiles) => {
    // Convert FileList to Array
    const filesArray = Array.from(selectedFiles);
    
    // Limit number of files if needed
    const filesToAdd = allowMultiple 
      ? filesArray.slice(0, maxFiles - files.length) 
      : [filesArray[0]];
    
    // Update files state
    const updatedFiles = allowMultiple ? [...files, ...filesToAdd] : filesToAdd;
    setFiles(updatedFiles);
    
    // Generate preview URLs
    const newPreviews = filesToAdd.map(file => URL.createObjectURL(file));
    const updatedPreviews = allowMultiple ? [...previewUrls, ...newPreviews] : newPreviews;
    setPreviewUrls(updatedPreviews);
    
    // Notify parent component about files if in delayed upload mode
    if (delayUpload && onFilesChange) {
      onFilesChange(updatedFiles);
    }
    
    // Notify parent component about preview URLs if in delayed upload mode
    if (delayUpload && onPreviewUrlsChange) {
      onPreviewUrlsChange(updatedPreviews);
    }
  }, [allowMultiple, files, maxFiles, previewUrls, delayUpload, onFilesChange, onPreviewUrlsChange]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  }, [handleFiles]);

  const handleFileInputChange = useCallback((e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  }, [handleFiles]);

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  const removeFile = (index) => {
    const updatedFiles = [...files];
    const updatedPreviews = [...previewUrls];
    
    // Revoke object URL to avoid memory leaks
    URL.revokeObjectURL(previewUrls[index]);
    
    // Remove the file and preview
    updatedFiles.splice(index, 1);
    updatedPreviews.splice(index, 1);
    
    setFiles(updatedFiles);
    setPreviewUrls(updatedPreviews);
    
    // Notify parent component about files if in delayed upload mode
    if (delayUpload && onFilesChange) {
      onFilesChange(updatedFiles);
    }
    
    // Notify parent component about preview URLs if in delayed upload mode
    if (delayUpload && onPreviewUrlsChange) {
      onPreviewUrlsChange(updatedPreviews);
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (files.length === 0) return;
    
    setUploading(true);
    const formData = new FormData();
    
    // Determine the endpoint to use
    const endpoint = uploadEndpoint || 
      (allowMultiple 
        ? `${config.backend_url}/api/add-seller-images/${data_id}`
        : `${config.backend_url}/api/update-seller-image/${data_id}`);
    
    // Append files to form data
    if (allowMultiple) {
      files.forEach((file, index) => {
        formData.append('files[]', file);
      });
    } else {
      formData.append('file', files[0]);
    }

    try {
      const response = await axios.request({
        method: allowMultiple ? 'post' : 'put',
        url: endpoint,
        data: formData,
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress({ total: percentCompleted });
        },
      });

      if (response.status === 200) {
        // Handle response based on mode (single/multiple)
        if (allowMultiple) {
          const { file_urls } = response.data;
          const newUploadedImages = [...uploadedImages, ...file_urls];
          setUploadedImages(newUploadedImages);
          onImagesUpdate(newUploadedImages);
        } else {
          const { image_url } = response.data;
          setUploadedImages([image_url]);
          onImagesUpdate(image_url);
        }
        
        // Reset states
        setFiles([]);
        setPreviewUrls([]);
        setUploadProgress({});
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      alert(t('smallWidgets.uploadError', { message: error.message }));
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteImage = async (imageUrl) => {
    const filename = imageUrl.split('/').pop();

    try {
      const response = await axios.delete(
        `${config.backend_url}/api/delete-seller-image/${data_id}`,
        {
          data: { filename },
          withCredentials: true,
        }
      );

      if (response.status === 200) {
        const updatedList = uploadedImages.filter((imgUrl) => imgUrl !== imageUrl);
        setUploadedImages(updatedList);
        onImagesUpdate(allowMultiple ? updatedList : null);
      }
    } catch (error) {
      console.error('Error deleting image:', error);
      alert(t('smallWidgets.deleteImageError'));
    }
  };

  // Add enhance image functionality
  const handleEnhanceImage = async (imageUrl) => {
    const filename = imageUrl.split('/').pop();

    try {
      const response = await axios.post(
        `${config.backend_url}/api/enhance-seller-image/${data_id}`,
        { filename },
        { withCredentials: true }
      );
      
      if (response.status === 200) {
        const { new_image_url } = response.data;

        // Replace the old URL in the uploadedImages array with the new URL
        const updatedImages = uploadedImages.map((img) =>
          img === imageUrl ? new_image_url : img
        );

        setUploadedImages(updatedImages);
        onImagesUpdate(allowMultiple ? updatedImages : new_image_url);
      }
    } catch (error) {
      console.error('Error enhancing image:', error);
      alert('Error enhancing image');
    }
  };

  return (
    <div className="unified-upload-container">
      <div 
        className={`drop-area ${isDragging ? 'dragging' : ''}`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={triggerFileInput}
      >
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleFileInputChange}
          accept={acceptTypes}
          multiple={allowMultiple}
        />
        {/* Camera input for mobile devices */}
        <input
          type="file"
          ref={cameraInputRef}
          style={{ display: 'none' }}
          onChange={handleCameraInputChange}
          accept="image/*"
          capture="environment"
          multiple={false}
        />
        <div className="drop-message">
          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="17 8 12 3 7 8"></polyline>
            <line x1="12" y1="3" x2="12" y2="15"></line>
          </svg>
          <p>{t('smallWidgets.uploadFiles')}</p>
          <p className="small">
            {allowMultiple 
              ? t('smallWidgets.uploadMultiple', { max: maxFiles }) 
              : t('smallWidgets.uploadSingle')}
          </p>
        </div>
      </div>

      {/* Camera button for mobile/tablet devices */}
      {isMobileOrTablet && (
        <div className="camera-button-container">
          <button 
            type="button" 
            className="camera-btn"
            onClick={handleCameraCapture}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
              <circle cx="12" cy="13" r="4"></circle>
            </svg>
            {t('smallWidgets.takePhoto')}
          </button>
        </div>
      )}

      {/* Preview area */}
      {previewUrls.length > 0 && (
        <div className="previews-area">
          <h4>{t('smallWidgets.selectedFiles')}</h4>
          <div className="preview-list">
            {previewUrls.map((url, idx) => (
              <div 
                key={idx} 
                className="preview-item"
                onClick={(e) => {
                  // Only trigger if we're clicking on the image area, not on a button
                  if (onImageClick && !e.target.closest('button')) {
                    e.stopPropagation();
                    onImageClick(url);
                  }
                }}
              >
                <img src={url} alt={t('smallWidgets.previewAlt', { index: idx + 1 })} />
                <button 
                  type="button" 
                  className="remove-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFile(idx);
                  }}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
          
          {!delayUpload && (
            uploading ? (
              <div className="progress-container">
                <div 
                  className="progress-bar" 
                  style={{ width: `${uploadProgress.total || 0}%` }}
                ></div>
                <span>{uploadProgress.total || 0}%</span>
              </div>
            ) : (
              <button 
                type="button" 
                className="upload-btn"
                onClick={handleSubmit}
                disabled={files.length === 0}
              >
                {t('smallWidgets.upload')}
              </button>
            )
          )}
        </div>
      )}

      {/* Uploaded images area */}
      {uploadedImages.length > 0 && (
        <div className="uploaded-images">
          <h4>{t('smallWidgets.uploadedImages')}</h4>
          <div className="preview-list">
            {uploadedImages.map((imgUrl, idx) => (
              <div 
                key={idx} 
                className="preview-item"
                onClick={(e) => {
                  // Only trigger if we're clicking on the image area, not on a button
                  if (onImageClick && !e.target.closest('button')) {
                    e.stopPropagation();
                    onImageClick(imgUrl);
                  }
                }}
              >
                <img src={imgUrl} alt={t('smallWidgets.uploadedImageAlt', { index: idx + 1 })} />
                <button 
                  type="button" 
                  className="remove-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteImage(imgUrl);
                  }}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default UnifiedFileUpload; 
